import {
  createContext,
  PropsWithChildren,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addProject,
  CreateProjectFields,
  getPaginatedProjects,
  getProject,
  getProjectsQuery,
  IProject,
  updateProject,
} from "integrations/firebase/collections";
import { logError } from "shared/services/ErrorReporting";
import { getCountFromServer } from "firebase/firestore";
import { ProjectType } from "reedy-data/models";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "contexts/AuthContext";
import { useAnalytics } from "contexts/AnalyticsContext";
import { usePagination } from "hooks/usePagination";

interface IProjectsContext {
  projects: IProject[];
  showOnlyMyProjects: boolean;
  setShowOnlyMyProjects: (value: boolean) => void;
  isLoadingRead: boolean;
  isLoadingCreate: boolean;
  isLoadingUpdate: boolean;
  errorRead?: Error;
  errorCreate?: Error;
  errorUpdate?: Error;
  updateProjectName: (idProject: string, name: string) => void;
  updateProjectIds: (idProject: string, productIds: string[]) => Promise<void>;
  setAmplifyProjectId: (idProject: string, amplifyProjectId: string) => void;
  getProject: (idProject: string) => Promise<IProject | undefined>;
  createProject: (
    name: string,
    productIds: string[],
    type?: ProjectType,
    searchOptions?: Record<string, string>,
  ) => Promise<string | undefined>;
  page: number;
  pageSize: number;
  count: number;

  fetchProjects(): Promise<void>;
}

export const ProjectsContext = createContext<IProjectsContext>(
  undefined as never,
);

export const ProjectsContextProvider = ({ children }: PropsWithChildren) => {
  const { user, organisation } = useSession();
  const pageDocsRef = useRef<IProject[]>([]);
  const [showOnlyMyProjects, setShowOnlyMyProjects] = useState(true);
  const { gaEvent } = useAnalytics();

  const [isLoadingRead, setIsLoadingRead] = useState(true);
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);

  const { pageSize, page, setPage } = usePagination();

  const [projects, setProjects] = useState<IProject[]>([]);

  const [errorRead, setErrorRead] = useState<Error>();
  const [errorCreate, setErrorCreate] = useState<Error>();
  const [errorUpdate, setErrorUpdate] = useState<Error>();

  const { data: count = 0 } = useQuery({
    queryKey: [
      "projects",
      organisation.id,
      showOnlyMyProjects ? user.id : null,
    ] as const,
    staleTime: 5 * 1000,
    async queryFn({ queryKey: [, organisationId, userId] }) {
      const serverCount = await getCountFromServer(
        getProjectsQuery(organisationId, userId),
      );

      return serverCount.data().count;
    },
  });

  const fetchProjects = useCallback(async () => {
    setIsLoadingRead(true);
    setErrorRead(undefined);

    try {
      const currentPageProjects = await getPaginatedProjects(
        organisation.id,
        showOnlyMyProjects ? user.id : null,
        { pageSize, currentPage: page - 1 },
      );

      if (currentPageProjects.length > 0) {
        pageDocsRef.current[page - 1] =
          currentPageProjects[currentPageProjects.length - 2];
      }

      setProjects(currentPageProjects);
    } catch (error) {
      logError(error);
      setErrorRead(error as Error);
    }

    setIsLoadingRead(false);
  }, [showOnlyMyProjects, page, organisation.id, pageSize, user.id]);

  const handleSetShowOnlyMyProjects = useCallback(
    (value: boolean) => {
      setShowOnlyMyProjects(value);
      setPage(1);
    },
    [setShowOnlyMyProjects, setPage],
  );

  useEffect(() => {
    // triggers the paginated projects fetch whenever its inputs change; the fetch sets its loading flag synchronously before awaiting the async request
    fetchProjects();
  }, [fetchProjects]);

  const fetchProject = useCallback(async (projectId: string) => {
    setIsLoadingRead(true);

    setErrorRead(undefined);
    let loadedProject;
    try {
      loadedProject = getProject(projectId);
    } catch (error) {
      logError(error);
      setErrorRead(error as Error);
    }

    setIsLoadingRead(false);
    return loadedProject;
  }, []);

  const getProjectOrFetch = useCallback(
    async (id: string) => {
      const existingProject = projects.find((p) => p.id === id);
      if (existingProject) return existingProject;
      return fetchProject(id);
    },
    [fetchProject, projects],
  );

  const createProject = useCallback(
    async (
      name: string,
      productIds: string[],
      type: ProjectType = ProjectType.ISBNS,
      searchOptions: Record<string, string> = {},
    ) => {
      setIsLoadingCreate(true);
      setErrorCreate(undefined);
      try {
        const newProjectData: CreateProjectFields = {
          name,
          type,
          productIds,
          userId: user.id,
          organisation: { id: organisation.id },
          createdAt: new Date(),
          searchOptions,
        };

        gaEvent({
          type: "create_project",
          payload: { books_count: productIds.length, type },
        });

        const newProjectRef = await addProject(newProjectData);
        const newProj = { ...newProjectData, id: newProjectRef.id };
        setProjects([newProj, ...projects]);
        setIsLoadingCreate(false);
        return newProjectRef.id;
      } catch (error) {
        logError(error);
        setErrorCreate(error as Error);
        setIsLoadingCreate(false);
      }
    },
    [user.id, organisation.id, projects, gaEvent],
  );

  const updateProjectName = useCallback(
    async (projectId: string, name: string) => {
      setIsLoadingUpdate(true);
      setErrorUpdate(undefined);
      try {
        await updateProject(projectId, { name });

        setProjects((current) =>
          current.map((p) => (p.id === projectId ? { ...p, name } : p)),
        );

        setIsLoadingUpdate(false);
      } catch (error) {
        logError(error);
        setErrorUpdate(error as Error);
        setIsLoadingUpdate(false);
      }
    },
    [],
  );

  const updateProjectIds = useCallback(
    async (projectId: string, productIds: string[]) => {
      setIsLoadingUpdate(true);
      setErrorUpdate(undefined);
      try {
        await updateProject(projectId, { productIds });
        setProjects((current) =>
          current.map((p) => (p.id === projectId ? { ...p, productIds } : p)),
        );
        setIsLoadingUpdate(false);
      } catch (error) {
        logError(error);
        setErrorUpdate(error as Error);
        setIsLoadingUpdate(false);
        throw error;
      }
    },
    [],
  );

  // The backend stores amplifyProjectId; mirroring it here lets the open
  // project page see it without a reload.
  const setAmplifyProjectId = useCallback(
    (projectId: string, amplifyProjectId: string) => {
      setProjects((current) =>
        current.map((p) =>
          p.id === projectId ? { ...p, amplifyProjectId } : p,
        ),
      );
    },
    [],
  );

  const projectsContextProviderValue = useMemo(
    () => ({
      projects,
      showOnlyMyProjects,
      setShowOnlyMyProjects: handleSetShowOnlyMyProjects,
      isLoadingRead,
      isLoadingCreate,
      isLoadingUpdate,
      errorRead,
      errorCreate,
      errorUpdate,
      updateProjectName,
      updateProjectIds,
      setAmplifyProjectId,
      page,
      pageSize,
      count,
      getProject: getProjectOrFetch,
      createProject,
      fetchProjects,
    }),
    [
      projects,
      showOnlyMyProjects,
      handleSetShowOnlyMyProjects,
      isLoadingRead,
      isLoadingCreate,
      isLoadingUpdate,
      errorRead,
      errorCreate,
      errorUpdate,
      updateProjectName,
      updateProjectIds,
      setAmplifyProjectId,
      page,
      pageSize,
      count,
      getProjectOrFetch,
      createProject,
      fetchProjects,
    ],
  );

  return (
    <ProjectsContext value={projectsContextProviderValue}>
      {children}
    </ProjectsContext>
  );
};

export const useProjects = () => {
  const ctxt = use(ProjectsContext);
  if (!ctxt) {
    throw new Error(
      "useProjects must be used within a ProjectsContextProvider",
    );
  }
  return ctxt;
};
